"use client"

import clsx from "clsx"
import Image from "next/image"
import React, { useEffect, useRef, useState } from "react"
import { TLoader } from "../display/TLoader"

/**
 * @description 미디어 오디오 플레이어
 * @example
 * ```tsx
 * <TMediaAudio src="/audio-thumb.jpg" aspectRatio="1/1" />
 * ```
 */
interface TMediaAudioProps {
	/** 오디오 썸네일 이미지 URL */
	src?: string
	/** 너비. 숫자(px) 또는 문자열("100%") */
	width?: number | string
	/** 높이. 숫자(px) 또는 문자열("100%") */
	height?: number | string
	/** CSS aspect-ratio 값 (예: "16/9", "1/1") */
	aspectRatio?: string
	/** CORS 설정 */
	crossOrigin?: "anonymous" | "use-credentials"
	/** 추가 CSS 클래스 */
	className?: string
}

export const TMediaAudio = React.memo((props: TMediaAudioProps) => {
	const { src, width = "100%", height = "100%", aspectRatio = "1/1", crossOrigin, className } = props

	const [loading, setLoading] = useState<boolean>(false)
	const imgRef = useRef<HTMLImageElement | null>(null)

	const handleImageLoad = React.useCallback(() => {
		setLoading(false)
	}, [])

	useEffect(() => {
		if (imgRef.current && imgRef.current.complete) {
			if (imgRef.current.naturalWidth > 0) {
				handleImageLoad()
			}
		}
	}, [])

	return (
		<section className={clsx("TMedia audio", className)} style={{ width, height, aspectRatio }}>
			{loading && <TLoader size={24} className="loader" />}

			<div className="thumb">
				<div className="thumb-inner">
					{src && (
						<Image ref={imgRef} src={src} fill crossOrigin={crossOrigin} alt="Audio thumbnail" onLoad={handleImageLoad} unoptimized style={{ objectFit: "cover" }} />
					)}
					<div className="dot">
						<div className="dot-inner" />
					</div>
				</div>
			</div>
		</section>
	)
})

TMediaAudio.displayName = "TMediaAudio"
